/**
 * Utilitaire centralisé pour gérer l'expiration de session
 * Utilisé par tous les dashboards pour une gestion uniforme
 */

/**
 * Vérifie si une erreur est liée à l'authentification (token expiré, invalide ou absent)
 * @param {Error} error - L'erreur à analyser
 * @returns {boolean} - true si c'est une erreur d'authentification
 */
export const isAuthenticationError = (error) => {
  // Vérification du statut HTTP 401
  if (error.response?.status === 401) {
    return true;
  }

  // Vérification des messages d'erreur liés au token
  const errorMessage = error.response?.data?.message || error.message || '';
  if (typeof errorMessage === 'string') {
    const lowerMessage = errorMessage.toLowerCase();
    return (
      lowerMessage.includes('token') ||
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('expired') ||
      lowerMessage.includes('invalid') ||
      lowerMessage.includes('authentification') ||
      lowerMessage.includes('authentication') ||
      lowerMessage.includes('session') ||
      lowerMessage.includes('limite') ||
      lowerMessage.includes('limit')
    );
  }

  return false;
};

let sessionExpirationHandled = false; // Flag to prevent multiple executions

// Reset flag when page loads (important for reload scenarios)
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    sessionExpirationHandled = false;
  });
  
  // Also reset on page visibility change (mobile scenarios)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      sessionExpirationHandled = false;
    }
  });
}

/**
 * Gère l'expiration de session de manière uniforme
 * - Efface toutes les données d'authentification
 * - Affiche un message à l'utilisateur
 * - Redirige vers la page de login
 * @param {string} customMessage - Message personnalisé (optionnel)
 */
export const handleSessionExpiration = (customMessage = null) => {
  if (sessionExpirationHandled) {
    console.log('🛑 [Auth] Session expiration déjà gérée, pas de nouvelle action');
    return; // Prevent multiple executions
  }
  sessionExpirationHandled = true;

  console.log('🚨 [Auth] Début de la gestion d\'expiration de session');

  // Arrêter immédiatement toutes les requêtes en cours
  if (window.abortAllRequests) {
    window.abortAllRequests();
  }

  // Nettoyer toutes les données d'authentification possibles
  const authKeys = [
    'authData',
    'authToken', 
    'superAdminToken',
    'authUser',
    'superAdminUser',
    'token',
    'refreshToken',
    'sessionId',
    'sessionData',
    'userId',
    'userRole',
    'tenantId',
    'organizationId',
    'userEmail',
    'lastGraphAnalysis', // Nettoyer aussi les données temporaires
    'nodementedOnboardingDone', // Optionnel : remettre à zéro l'onboarding
  ];

  authKeys.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Ignorer les erreurs de localStorage (mode privé, etc.)
    }
  });

  // Nettoyer aussi le stockage sécurisé
  try {
    const secureKeys = Object.keys(localStorage).filter(key => key.startsWith('secure_nodemented_'));
    secureKeys.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    // Ignorer les erreurs
  }

  // Nettoyer aussi les cookies si possible
  try {
    document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'sessionId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  } catch (e) {
    // Ignorer les erreurs de cookies
  }

  // Message utilisateur adapté pour mobile
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  const baseMessage = customMessage || 'Session expirée ou non authentifiée. Veuillez vous reconnecter.';
  const mobileMessage = isMobile ? 
    baseMessage + '\n\n💡 Sur mobile, fermez tous les onglets de l\'application avant de vous reconnecter.' : 
    baseMessage;
  
  alert(mobileMessage);

  console.log('🚨 [Auth] Redirection programmée vers /');
  
  // Redirection avec une vérification supplémentaire pour mobile
  setTimeout(() => {
    console.log('🚨 [Auth] Exécution de la redirection...');
    
    // Forcer le nettoyage du cache mobile
    if (isMobile && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }
    
    // Redirection forcée - double sécurité
    try {
      window.location.replace('/');
    } catch (e) {
      // Fallback si replace ne fonctionne pas
      window.location.href = '/';
    }
    
    // Triple sécurité - forcer le reload si toujours sur la même page après 500ms
    setTimeout(() => {
      if (window.location.pathname !== '/') {
        console.log('🚨 [Auth] Redirection forcée - reload');
        window.location.reload();
      }
    }, 500);
    
  }, isMobile ? 2000 : 100); // Plus de temps sur mobile pour lire le message
};

/**
 * Fonction principale à utiliser dans les catch des dashboards
 * Combine la détection et la gestion d'erreur d'authentification
 * @param {Error} error - L'erreur à traiter
 * @param {string} customMessage - Message personnalisé (optionnel)
 * @returns {boolean} - true si c'était une erreur d'auth (et qu'elle a été gérée)
 */
export const handleAuthError = (error, customMessage = null) => {
  if (isAuthenticationError(error)) {
    handleSessionExpiration(customMessage);
    return true;
  }
  return false;
};

/**
 * Middleware pour intercepter les erreurs d'authentication dans apiClient
 * Peut être utilisé comme intercepteur Axios pour une gestion automatique
 */
export const createAuthErrorInterceptor = () => {
  return (error) => {
    if (isAuthenticationError(error)) {
      // Éviter de rediriger si on est déjà sur une page d'auth
      const currentPath = window.location.pathname;
      if (currentPath !== '/' && !currentPath.includes('/auth') && !currentPath.includes('/login')) {
        console.log('🚨 [Auth Interceptor] Session expirée détectée, déclenchement de la gestion...');
        handleSessionExpiration();
      }
      return Promise.reject(new Error('Session expirée - redirection en cours'));
    }
    return Promise.reject(error);
  };
};

export default {
  isAuthenticationError,
  handleSessionExpiration, 
  handleAuthError,
  createAuthErrorInterceptor
};
